import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import './MenuPancake.css';
import AppStarLogo from '../images/appstar_title_tr.png';
import AppStarQrCode from '../images/appstar.zylascope.com-qr.png';
import axios from 'axios';
import { serverDomain } from '../pages/config';
import { createNewAppRequest, setCurrentAppContext } from './appCreationUtils';
import { clearStoredProfileState } from './profileIconUtils';

export default function MenuPancake({ bgColor = '#222', textColor = '#fff', isMobile = false, showBrand = true }) {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [menuAnimating, setMenuAnimating] = useState(false); // controls fly-in class
  const closingTimerRef = useRef(null);
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem('appstar_admin') === 'true';

  const clearSessionStorage = () => {
    localStorage.removeItem('appstar_jwt');
    localStorage.removeItem('appstar_user_id');
    localStorage.removeItem('appstar_app_id');
    localStorage.removeItem('appstar_app_name');
    localStorage.setItem('appstar_admin', 'false');
    clearStoredProfileState();
  };

  const handleLogout = () => {
    const token = localStorage.getItem('appstar_jwt');
    console.log('Logging out with token:', token);
    if (!token) return;
    axios
      .post(serverDomain + 'force_log_user_out.php', null, { params: { token } })
      .then((response) => {
        if (response.data && response.data.success === 1) {
          clearSessionStorage();
          setOpen(false);
          navigate('/login', { state: { loggedOut: true } });
        } else {
          console.log('Logout failed:', response.data);
          setToast('Could not log out at this time');
          setTimeout(() => setToast(''), 3000);
        }
      })
      .catch(() => {
        setToast('Could not log out at this time');
        setTimeout(() => setToast(''), 3000);
      });
  };

  const handleCreateNewApp = () => {
    const token = localStorage.getItem('appstar_jwt');
    if (!token) {
      navigate('/login');
      return;
    }

    createNewAppRequest(token)
      .then((response) => {
        setCurrentAppContext({ appId: response.app_id, appName: response.app_name });
        setOpen(false);
        navigate('/chat');
      })
      .catch(() => {
        setToast('App could not be created at the moment');
        setTimeout(() => setToast(''), 3000);
      });
  };

  useEffect(() => {
    function handleDocumentMouseDown(event) {
      if (!open) return;
      const w = wrapperRef.current;
      const m = menuRef.current;
      if (m && m.contains(event.target)) return; // clicks inside menu allowed
      if (w && w.contains(event.target)) return; // clicks on trigger/logo allowed
      startClose();
    }
    
    function handleWindowBlur() {
      // Close menu when window loses focus (e.g., clicking on iframe)
      setOpen(false);
    }
    
    if (open) {
      document.addEventListener('mousedown', handleDocumentMouseDown);
      window.addEventListener('blur', handleWindowBlur);
    } else {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      window.removeEventListener('blur', handleWindowBlur);
    }
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [open]);

  // Manage fly-in animation: add 'open' class after initial mount off-screen
  useEffect(() => {
    if (open) {
      setMenuAnimating(false); // start hidden (left:-200px)
      // Double rAF to ensure initial position styles apply before class change
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setMenuAnimating(true));
      });
    } else {
      setMenuAnimating(false);
    }
    return () => {
      if (closingTimerRef.current) {
        clearTimeout(closingTimerRef.current);
        closingTimerRef.current = null;
      }
    };
  }, [open]);

  function startClose() {
    // Remove open class to trigger slide-out while keeping mounted
    setMenuAnimating(false);
    if (closingTimerRef.current) clearTimeout(closingTimerRef.current);
    closingTimerRef.current = setTimeout(() => {
      setOpen(false);
      closingTimerRef.current = null;
    }, 300); // match CSS transition duration
  }

  return (
    <div ref={wrapperRef} className="menu-pancake-wrapper" style={{ display: 'flex', alignItems: 'center' }}>
      <button className="menu-pancake" onClick={() => {
        if (open) {
          startClose();
        } else {
          setOpen(true);
        }
      }} style={{ color: textColor, background: 'none', border: 'none' }}>
        &#9776;
      </button>
      {/* Inline logo/title in the AdminBar for both desktop and mobile */}
      {showBrand && (
        <>
          <img src={AppStarLogo} alt="AppStar" style={{ width: 28, height: 'auto', marginLeft: '0.75rem', marginRight: '0.5rem', display: 'block' }} />
          <span style={{ color: textColor, fontSize: '1rem', fontWeight: '400' }}>AppStar</span>
        </>
      )}
      {isMobile && open && createPortal(
        <>
          <div
            className={`menu-backdrop ${menuAnimating ? 'open' : ''}`}
            onClick={startClose}
            aria-label="Close menu backdrop"
          />
          <nav ref={menuRef} className={`menu ${menuAnimating ? 'open' : ''}`} style={{ background: bgColor }}>
            {toast && (
              <div style={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                background: '#333',
                color: '#fff',
                padding: '12px 16px',
                borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                zIndex: 1100,
                fontSize: '0.9rem'
              }}>{toast}</div>
            )}
            {/* Mobile menu header: first row with logo/title on the left and close button on the right */}
            <div className="menu-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.15)', height: 56, boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={AppStarLogo} alt="AppStar" style={{ width: 28, height: 'auto', display: 'block' }} />
                <span style={{ color: textColor, fontSize: '1rem', fontWeight: 400 }}>AppStar</span>
              </div>
              <button className="menu-close" onClick={startClose} aria-label="Close menu" style={{ color: textColor }}>
                &times;
              </button>
            </div>
            <ul>
              <li><Link to="/" style={{ color: textColor }} onClick={() => setOpen(false)}>Home</Link></li>
              <li><Link to="/chat" style={{ color: textColor }} onClick={() => setOpen(false)}>Chat</Link></li>
              <li><button type="button" style={{ color: textColor }} onClick={handleCreateNewApp}>New app</button></li>
              <li><Link to="/account" style={{ color: textColor }} onClick={() => setOpen(false)}>Account</Link></li>
              {isAdmin && <li><Link to="/update-credit" style={{ color: textColor }} onClick={() => setOpen(false)}>Credit</Link></li>}
              {isAdmin && <li><Link to="/token-cost" style={{ color: textColor }} onClick={() => setOpen(false)}>Token cost</Link></li>}
              <li><Link to="/login" style={{ color: textColor }} onClick={() => setOpen(false)}>Login</Link></li>
              <li><Link to="/signup" style={{ color: textColor }} onClick={() => setOpen(false)}>Sign Up</Link></li>
              {localStorage.getItem('appstar_jwt') && (
                <li><Link to="#" style={{ color: textColor }} onClick={(e) => { e.preventDefault(); handleLogout(); }}>Log out</Link></li>
              )}
              <li style={{ color: textColor, padding: '10px 20px', textAlign: 'center' }}>Share</li>
              <div className="menu-qr-dock">
                <img src={AppStarQrCode} alt="AppStar QR Code" className="menu-qr-image" />
              </div>
              <li style={{ color: textColor, opacity: 0.8, padding: '10px 20px', fontSize: '0.6rem' }}>M: GPT-5.1</li>
            </ul>
          </nav>
        </>,
        document.body
      )}
      {!isMobile && (
        <nav ref={menuRef} className={`menu ${open ? (menuAnimating ? 'open' : '') : ''}`} style={{ background: bgColor }}>
          {toast && (
            <div style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              background: '#333',
              color: '#fff',
              padding: '12px 16px',
              borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
              zIndex: 1100,
              fontSize: '0.9rem'
            }}>{toast}</div>
          )}
          <button className="menu-close" onClick={startClose} aria-label="Close menu" style={{ color: textColor }}>
            &times;
          </button>
          <ul>
            <li><Link to="/" style={{ color: textColor }} onClick={startClose}>Home</Link></li>
            <li><Link to="/chat" style={{ color: textColor }} onClick={startClose}>Chat</Link></li>
            <li><button type="button" style={{ color: textColor }} onClick={handleCreateNewApp}>New app</button></li>
            <li><Link to="/account" style={{ color: textColor }} onClick={startClose}>Account</Link></li>
            {isAdmin && <li><Link to="/update-credit" style={{ color: textColor }} onClick={startClose}>Credit</Link></li>}
            {isAdmin && <li><Link to="/token-cost" style={{ color: textColor }} onClick={startClose}>Token cost</Link></li>}
            <li><Link to="/login" style={{ color: textColor }} onClick={startClose}>Login</Link></li>
            <li><Link to="/signup" style={{ color: textColor }} onClick={startClose}>Sign Up</Link></li>
            {localStorage.getItem('appstar_jwt') && (
              <li><Link to="#" style={{ color: textColor }} onClick={(e) => { e.preventDefault(); handleLogout(); }}>Log out</Link></li>
            )}
            <li style={{ color: textColor, padding: '10px 20px', textAlign: 'center' }}>Share</li>
            <div className="menu-qr-dock">
              <img src={AppStarQrCode} alt="AppStar QR Code" className="menu-qr-image" />
            </div>
            <li style={{ color: textColor, opacity: 0.8, padding: '10px 20px', fontSize: '0.6rem' }}>M: GPT-5.1</li>
          </ul>
        </nav>
      )}
    </div>
  );
}
